/**
 * The public results page (/s/<token>) in a brand's look: its theme colors in
 * dark and light mode, its fonts and its layout style, as members see the app.
 *
 * Same idea as the app's themes (utils/brandTheme.js): each of the page's
 * standard shades becomes the brand's hue at the SAME luminance, so every
 * contrast stays what the standard page has, whatever the brand's colors.
 * The accent (buttons, selected pills) is the brand's main color, made
 * lighter or darker only as far as it needs to stand out from the cards.
 */
import { cleanHex, contrast, hueSat, luminance, shadeAt } from './brandTheme';
import { BODY_FONTS, HEADING_FONTS, findFont, fontStack, loadFont } from './brandFonts';

/** The standard page's shades (PublicSharePage.css), per mode. */
const BASE = {
  dark: { bg: '#0b1120', card: '#111827', border: '#1f2937', grid: '#1f2937', gridStrong: '#334155', text: '#e5e7eb', muted: '#94a3b8', heroFrom: '#12213d' },
  light: { bg: '#f6f7f9', card: '#ffffff', border: '#e5e7eb', grid: '#eef2f7', gridStrong: '#cbd5e1', text: '#0f172a', muted: '#64748b', heroFrom: '#eef4ff' },
};

const MONO = {
  dark: { bg: '#0a0a0a', card: '#141414', border: '#262626', grid: '#262626', gridStrong: '#404040', text: '#f5f5f5', muted: '#a3a3a3', heroFrom: '#1f1f1f', accent: '#f5f5f5' },
  light: { bg: '#f7f7f7', card: '#ffffff', border: '#e5e5e5', grid: '#efefef', gridStrong: '#d4d4d4', text: '#0a0a0a', muted: '#6b6b6b', heroFrom: '#f0f0f0', accent: '#111111' },
};

const readableOn = (color) => (contrast(color, '#ffffff') >= contrast(color, '#0b0f19') ? '#ffffff' : '#0b0f19');

/** The brand color, lightened or darkened until it has at least 3:1 against `card`. */
export function accentFor(color, card, mode) {
  if (contrast(color, card) >= 3) return color;
  const [h, s] = hueSat(color);
  const cardLum = luminance(card);
  // 3:1 with a little room: above the card in dark mode, below it in light mode.
  const target = mode === 'dark' ? (cardLum + 0.05) * 3.2 - 0.05 : (cardLum + 0.05) / 3.2 - 0.05;
  return shadeAt(h, s, target);
}

/**
 * CSS variables for the page in `mode` ('dark' | 'light'), or {} for the
 * standard look. theme: the brand theme ({ primary, secondary?, accent?,
 * background?, style? }); accentOverride: the link's own accent color.
 */
export function shareLookVars(theme, mode, accentOverride = null) {
  const m = mode === 'light' ? 'light' : 'dark';
  const vars = {};
  const override = cleanHex(accentOverride);
  if (!theme || theme.enabled === false || (!cleanHex(theme.primary) && theme.style !== 'mono')) {
    if (override) { vars['--psp-accent'] = override; vars['--psp-accent-text'] = readableOn(override); }
    return vars;
  }
  let shades;
  // Black & white with a highlight color: buttons stay black / white, selected
  // options take the highlight (as small highlights do in the app).
  const monoHighlight = theme.style === 'mono' ? cleanHex(theme.accent) : null;
  if (theme.style === 'mono') {
    shades = { ...MONO[m] };
  } else {
    const base = BASE[m];
    const primary = cleanHex(theme.primary);
    const [ph, ps] = hueSat(primary);
    // Surfaces take the background color's hue if set, else the main color's,
    // kept calm (low saturation) unless the brand chose a background itself.
    const bgColor = cleanHex(theme.background);
    const [bh, bsRaw] = hueSat(bgColor || primary);
    const bs = bgColor ? bsRaw : Math.min(bsRaw, m === 'dark' ? 0.45 : 0.3);
    const tint = (hex, cap = 1) => shadeAt(bh, Math.min(bs, cap), luminance(hex));
    shades = {
      bg: tint(base.bg),
      card: m === 'light' ? '#ffffff' : tint(base.card),
      border: tint(base.border, 0.35),
      grid: tint(base.grid, 0.35),
      gridStrong: tint(base.gridStrong, 0.3),
      text: tint(base.text, 0.15),
      muted: tint(base.muted, 0.25),
      heroFrom: shadeAt(ph, ps * 0.6, luminance(base.heroFrom)),
      accent: primary,
    };
  }
  const accent = accentFor(override || shades.accent, shades.card, m);
  Object.assign(vars, {
    '--psp-bg': shades.bg,
    '--psp-card': shades.card,
    '--psp-border': shades.border,
    '--psp-grid': shades.grid,
    '--psp-grid-strong': shades.gridStrong,
    '--psp-text': shades.text,
    '--psp-muted': shades.muted,
    '--psp-hero': `linear-gradient(135deg, ${shades.heroFrom}, ${shades.card})`,
    '--psp-accent': accent,
    '--psp-accent-text': readableOn(accent),
  });
  if (monoHighlight && !override) {
    const highlight = accentFor(monoHighlight, shades.card, m);
    vars['--psp-highlight'] = highlight;
    vars['--psp-highlight-text'] = readableOn(highlight);
  }
  return vars;
}

/**
 * The brand's fonts and layout style for the page: { className, vars }.
 * layout: branding.layout ({ preset, fonts: { heading, body }, defaultMode }).
 */
export function shareLayout(layout) {
  const preset = layout?.preset === 'clean' || layout?.preset === 'sharp' ? layout.preset : 'classic';
  const vars = {};
  // A layout's own font, unless the brand picked a body font.
  const layoutBody = { clean: 'manrope', sharp: 'ibm-plex-sans' }[preset];
  const body = findFont(BODY_FONTS, layout?.fonts?.body && layout.fonts.body !== 'default' ? layout.fonts.body : layoutBody);
  const heading = findFont(HEADING_FONTS, layout?.fonts?.heading);
  [['--psp-font', body], ['--psp-heading-font', heading]].forEach(([name, font]) => {
    const stack = fontStack(font);
    if (stack) { loadFont(font); vars[name] = stack; }
  });
  const tight = layout?.headingStyle === 'tight' ? ' psp--tight' : '';
  return { className: `${preset === 'classic' ? '' : ` psp--${preset}`}${tight}`, vars };
}

/** First mode: the brand's first look if set, else the viewer's setting. */
export function initialShareMode(layout) {
  if (layout?.defaultMode === 'light' || layout?.defaultMode === 'dark') return layout.defaultMode;
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch (_e) {
    return 'dark';
  }
}
